import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, inr, fmtDate } from "../lib/api";

const BADGE = { add: "bg-emerald-600", edit: "bg-amber-500", delete: "bg-red-600" };
const LABEL = { add: "Added", edit: "Edited", delete: "Deleted" };
const FIELD = { amount: "Amount", date: "Date", description: "Description", mode: "Mode", type: "Type", branch: "Branch" };

const fmtVal = (k, v) => (k === "amount" ? inr(v) : k === "date" ? fmtDate(v) : v || "–");

const Changes = ({ changes }) => (
  <ul className="text-xs text-ink2 space-y-0.5">
    {Object.entries(changes).map(([k, c]) => (
      <li key={k}><span className="text-muted-c">{FIELD[k] || k}:</span> {fmtVal(k, c.from)} → <span className="text-ink">{fmtVal(k, c.to)}</span></li>
    ))}
  </ul>
);

const Filter = ({ value, onChange, items, testid }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="rounded-none w-full sm:w-52 bg-canvas border-line2" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const ActivityLog = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [who, setWho] = useState("");
  const [action, setAction] = useState("");

  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)); }, []);
  useEffect(() => {
    api.get("/activity", { params: { user_id: who || undefined, action: action || undefined } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [who, action]);

  const people = [["all", "Everyone"], [user.id, `${user.name || "Admin"} (you)`], ...staff.map((s) => [s.id, s.name])];
  const actions = [["all", "All actions"], ["add", "Added"], ["edit", "Edited"], ["delete", "Deleted"]];

  return (
    <div className="space-y-6" data-testid="activity-log">
      <header className="rise">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-c">Admin</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Activity log</h1>
      </header>
      <div className="flex flex-col sm:flex-row gap-3">
        <Filter value={who} onChange={setWho} items={people} testid="activity-user-filter" />
        <Filter value={action} onChange={setAction} items={actions} testid="activity-action-filter" />
      </div>
      {rows.length === 0 ? (
        <div className="bg-surface border border-dashed border-line2 p-10 text-center" data-testid="activity-empty">
          <History className="h-8 w-8 mx-auto text-muted-c" />
          <p className="mt-2 text-sm text-muted-c">No activity yet</p>
        </div>
      ) : (
        <div className="bg-surface border border-line overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-head border-b border-line">
              <tr className="font-mono text-[11px] uppercase tracking-wider text-muted-c text-left">
                <th className="px-4 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Book / Branch</th><th className="px-4 py-3">Entry</th><th className="px-4 py-3">Changes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line/60 row-hover align-top" data-testid={`activity-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="px-4 py-3 font-medium">{r.user_name}</td>
                  <td className="px-4 py-3"><span className={`text-xs text-white px-2 py-0.5 ${BADGE[r.action]}`}>{LABEL[r.action]}</span></td>
                  <td className="px-4 py-3">{r.site_name}<span className="text-muted-c"> / {r.branch_name}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`font-mono ${r.type === "deposit" ? "text-pos" : "text-neg"}`}>{r.type === "deposit" ? "+" : "−"}{inr(r.amount)}</span>
                    <span className="text-xs text-muted-c ml-2">{fmtDate(r.date)}</span>
                    {r.description && <p className="text-xs text-ink2">{r.description}</p>}
                  </td>
                  <td className="px-4 py-3">{r.action === "edit" ? <Changes changes={r.changes} /> : <span className="text-muted-c">–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
